/**
 * One place that decides how a person is named in the UI.
 *
 * Five components each derived this for themselves and disagreed. The worst
 * case was the dashboard greeting, which read `currentUser.full_name` — a field
 * the API never sends. `/api/users/me` returns `schemas.UserDetail`, which
 * carries `first_name` and `last_name`; `full_name` exists only as a property
 * on the backend model. So the greeting always fell through to the email's
 * local part and showed "Hi, r.aghdasi" where it meant "Hi, Rasool".
 *
 * People are addressed by first name throughout. Use `fullName` only where two
 * colleagues genuinely have to be told apart.
 */

/**
 * "bob.adam@acme.com" -> "Bob". Last resort when no name is on record.
 * A one-letter leading token is an initial, not a name ("r.aghdasi"), so the
 * next token is used instead — a surname reads better than a bare "R".
 */
const fromEmail = (email) => {
  if (!email) return '';
  const local = String(email).split('@')[0];
  const parts = local.split(/[._+-]/).filter(Boolean);
  const pick = (parts[0] && parts[0].length === 1 && parts[1]) ? parts[1] : (parts[0] || local);
  return pick ? pick.charAt(0).toUpperCase() + pick.slice(1) : '';
};

/**
 * The name to greet and label someone by, everywhere.
 * Accepts both shapes we receive: the split first_name/last_name of
 * /api/users/me, and the single full_name some list endpoints return.
 */
export const firstName = (user, fallback = 'there') => {
  if (!user) return fallback;
  const given = (user.first_name || '').trim();
  if (given) return given;
  const full = (user.full_name || '').trim();
  if (full) return full.split(/\s+/)[0];
  return fromEmail(user.email) || fallback;
};

/** Both names, for the few places that must distinguish one person from another. */
export const fullName = (user, fallback = 'User') => {
  if (!user) return fallback;
  const both = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  if (both) return both;
  const full = (user.full_name || '').trim();
  if (full) return full;
  return fromEmail(user.email) || user.email || fallback;
};

/** One or two letters for an avatar. */
export const userInitials = (user) => {
  if (!user) return '?';
  const f = (user.first_name || '').trim();
  const l = (user.last_name || '').trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  if (f) return f[0].toUpperCase();
  const full = (user.full_name || '').trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length > 1
      ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
      : parts[0][0].toUpperCase();
  }
  const fromMail = fromEmail(user.email);
  return fromMail ? fromMail[0].toUpperCase() : '?';
};
